import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Header from "@/components/layout/Header";
import API, { formatPrice, getImageUrl } from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { Copy, Upload, Download, CheckCircle, Image, MapPin, Package, Home, Building2, Truck, CreditCard, ChevronRight, XCircle, SplitSquareVertical, Plus, Minus, Trash2, Clock } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";

const getDisplayProductLabel = (productName = "", brandName = "") => {
  const rawName = String(productName || "").trim();
  const cleanBrandName = String(brandName || "").trim();
  if (!rawName) return cleanBrandName || "Produk";
  const nameWithoutLegacyBrand = rawName.replace(/\s+by\s+.+$/i, "").trim();
  if (!cleanBrandName || cleanBrandName === "No Brand") return nameWithoutLegacyBrand || rawName;
  return `${nameWithoutLegacyBrand || rawName} by ${cleanBrandName}`;
};

const getDisplayVariantValues = (variant = {}) => {
  const technicalKeys = new Set(["variant_id", "product_id", "weight_gram", "weight", "sku", "stock", "price", "cost_price", "size_price_adjustment", "sort_order", "status", "source_hash", "created_at", "updated_at", "pricing"]);
  return Object.entries(variant || {})
    .filter(([key, value]) => !technicalKeys.has(key) && value !== null && value !== undefined && String(value).trim() !== "")
    .map(([, value]) => String(value));
};

const PageWrapper = ({ children, isLoading = false }) => {
  const { t } = useLanguage();
  return (
    <div className="min-h-screen bg-stone-50 pb-20">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24 lg:pt-32">
        <Link to="/orders" className="inline-flex items-center text-sm font-medium text-stone-500 hover:text-stone-900 mb-6 transition-colors group">
          <ChevronRight className="w-4 h-4 mr-1 rotate-180 transition-transform group-hover:-translate-x-1" />
          {t("orders.back_to_list") || "Kembali ke Daftar Pesanan"}
        </Link>
        {isLoading ? (
          <div className="animate-pulse space-y-6">
            <div className="h-20 bg-white border border-stone-200 rounded-xl"></div>
            <div className="h-64 bg-white border border-stone-200 rounded-xl"></div>
          </div>
        ) : children}
      </div>
    </div>
  );
};

export default function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, locale } = useLanguage();
  const [order, setOrder] = useState(null);
  const [installments, setInstallments] = useState([]);
  const [products, setProducts] = useState({}); // To fetch dp_percentage & stock_type
  const [orderStatuses, setOrderStatuses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modals state
  const [uploading, setUploading] = useState(false);
  const [addressForm, setAddressForm] = useState({ name: "", phone: "", address: "", sender_type: "self", sender_name: "", sender_phone: "" });
  const [isAddingAddress, setIsAddingAddress] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showPOEditor, setShowPOEditor] = useState(false);
  const [poEditorItems, setPOEditorItems] = useState([]);
  const [savingPOEditor, setSavingPOEditor] = useState(false);

  // Split Orders State
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitFile, setSplitFile] = useState(null);
  const [uploadingSplit, setUploadingSplit] = useState(false);
  const [splitMode, setSplitMode] = useState("excel"); // excel atau manual
  const [manualShipments, setManualShipments] = useState([]); // [{id: 1, name: "", phone: "", address: "", items: []}]

  // Bank Info
  const [rekenings, setRekenings] = useState([]);
  const [confirmations, setConfirmations] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch order details
        const resOrder = await API.get(`/orders/${id}`);
        const orderData = resOrder.data;
        setOrder(orderData);

        // Auto populate address form if already exists
        let shipData = {};
        if (orderData.shipping && typeof orderData.shipping === "object") {
          shipData = orderData.shipping;
        } else if (orderData.shipping && typeof orderData.shipping === "string") {
          try { shipData = JSON.parse(orderData.shipping); } catch { }
        }
        if (shipData.name) {
          let sType = orderData.sender_type || "self";
          let sName = "";
          let sPhone = "";
          if (orderData.sender_info && typeof orderData.sender_info === "object") {
            sName = orderData.sender_info.name || "";
            sPhone = orderData.sender_info.phone || "";
          }
          setAddressForm({ name: shipData.name, phone: shipData.phone, address: shipData.address, sender_type: sType, sender_name: sName, sender_phone: sPhone });
        }

        // Fetch installments
        const resInst = await API.get(`/installments/${id}`);
        setInstallments(resInst.data.installments || []);

        // Fetch product info to know stock_type and DP rules
        const prodData = {};
        for (const item of orderData.items || []) {
          if (!prodData[item.product_id]) {
            try {
              const pReq = await API.get(`/products/${item.product_id}`);
              prodData[item.product_id] = pReq.data;
            } catch (e) {
              // fallback
              prodData[item.product_id] = { stock_type: "ready_stock", dp_percentage: 50 };
            }
          }
        }
        setProducts(prodData);

        // Fetch Bank accounts
        const rekRes = await API.get("/rekenings");
        setRekenings(rekRes.data.rekenings || []);

        // Fetch Confirmations
        const confRes = await API.get(`/orders/${id}/confirmations`);
        setConfirmations(confRes.data.confirmations || []);

        // Fetch Statuses for translations
        const sRes = await API.get("/order-statuses").catch(() => null);
        if (sRes) setOrderStatuses(sRes.data.statuses || []);

      } catch (err) {
        toast.error("Gagal memuat detail pesanan.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleConfirmPayment = async (e, type) => {
    if (type === 'settlement' && shouldLockSettlementForShipping) {
      toast.error('Atur pengiriman dulu sebelum upload pelunasan.');
      e.target.value = null;
      return;
    }
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("transaction_id", id);
      formData.append("payment_type", type);
      // Nominal is handled by admin during verification as per user request

      await API.post(`/confirmations`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("order_detail.conf_submitted"));

      // Refresh order + confirmations so PO edit lock updates immediately after upload
      const [orderRes, confRes] = await Promise.all([
        API.get(`/orders/${id}`),
        API.get(`/orders/${id}/confirmations`),
      ]);
      setOrder(orderRes.data);
      setConfirmations(confRes.data.confirmations || []);
      setShowPOEditor(false);
      setPOEditorItems([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal kirim konfirmasi.");
    } finally {
      setUploading(false);
      e.target.value = null;
    }
  };

  const handleSaveAddress = async () => {
    if (!addressForm.name || !addressForm.phone || !addressForm.address) {
      toast.error("Harap lengkapi semua kolom alamat penerima.");
      return;
    }
    if (addressForm.sender_type === "dropship" && (!addressForm.sender_name || !addressForm.sender_phone)) {
      toast.error("Harap lengkapi semua kolom pengirim dropship.");
      return;
    }
    try {
      await API.put(`/orders/${id}/address`, addressForm);
      toast.success("Alamat pengiriman berhasil disimpan.");
      setIsAddingAddress(false);
      const resOrder = await API.get(`/orders/${id}`);
      setOrder(resOrder.data);
    } catch (err) {
      toast.error("Gagal menyimpan alamat.");
    }
  };

  const handleCancelOrder = async () => {
    if (!window.confirm(t("orders.cancel_confirm"))) return;
    setCancelling(true);
    try {
      await API.post(`/orders/${id}/cancel`);
      toast.success(t("orders.cancel_success"));
      const resOrder = await API.get(`/orders/${id}`);
      setOrder(resOrder.data);
    } catch (err) {
      toast.error("Gagal membatalkan pesanan");
    } finally {
      setCancelling(false);
    }
  };

  const editablePOItems = (order?.items || []).filter(item => item.stock_type === "preorder");
  const editablePOProductIds = [...new Set(editablePOItems.map(item => item.product_id).filter(Boolean))];
  const editablePOProductId = editablePOProductIds.length === 1 ? editablePOProductIds[0] : null;
  const editablePOProduct = editablePOProductId ? products[editablePOProductId] : null;

  const buildPOEditorRow = (item = {}, fallbackProductId = editablePOProductId) => ({
    editor_id: item.editor_id || item.transaction_detail_id || `new-${crypto.randomUUID()}`,
    transaction_detail_id: item.transaction_detail_id || null,
    product_id: item.product_id || fallbackProductId || null,
    style: item.variant?.style || item.style || "",
    color: item.variant?.color || item.color || "",
    size: item.variant?.size || item.size || "",
    quantity: Number(item.quantity || 1),
    item_notes: item.item_notes || "",
  });

  const createDefaultPOEditorRow = () => {
    const variants = Array.isArray(editablePOProduct?.variants) ? editablePOProduct.variants : [];
    const firstVariant = variants[0] || {};
    return buildPOEditorRow({
      transaction_detail_id: null,
      product_id: editablePOProductId,
      style: firstVariant.style || "",
      color: firstVariant.color || "",
      size: firstVariant.size || "",
      quantity: 1,
      item_notes: "",
    }, editablePOProductId);
  };

  const openPOOrderEditor = () => {
    if (!editablePOProductId) {
      toast.error("Produk PO untuk pesanan ini tidak valid.");
      return;
    }
    setPOEditorItems(editablePOItems.map(item => buildPOEditorRow(item, editablePOProductId)));
    setShowPOEditor(true);
  };

  const closePOOrderEditor = () => {
    setShowPOEditor(false);
    setPOEditorItems([]);
  };

  const getPOEditorVariantOptions = (row) => {
    const variants = Array.isArray(products[row.product_id || editablePOProductId]?.variants) ? products[row.product_id || editablePOProductId].variants : [];
    const styles = [...new Set(variants.map(v => v?.style).filter(Boolean))];
    const colors = [...new Set(variants.filter(v => !row.style || v?.style === row.style).map(v => v?.color).filter(Boolean))];
    const sizes = [...new Set(variants.filter(v => (!row.style || v?.style === row.style) && (!row.color || v?.color === row.color)).map(v => v?.size).filter(Boolean))];
    return { styles, colors, sizes };
  };

  const updatePOEditorField = (editorId, field, value) => {
    setPOEditorItems(prev => prev.map(row => {
      if (row.editor_id !== editorId) return row;
      const next = { ...row, [field]: value };
      const variants = Array.isArray(products[row.product_id || editablePOProductId]?.variants) ? products[row.product_id || editablePOProductId].variants : [];

      if (field === "style") {
        const validColors = new Set(variants.filter(v => !next.style || v?.style === next.style).map(v => v?.color).filter(Boolean));
        if (next.color && validColors.size > 0 && !validColors.has(next.color)) next.color = "";
        const validSizes = new Set(variants.filter(v => (!next.style || v?.style === next.style) && (!next.color || v?.color === next.color)).map(v => v?.size).filter(Boolean));
        if (next.size && validSizes.size > 0 && !validSizes.has(next.size)) next.size = "";
      }

      if (field === "color") {
        const validSizes = new Set(variants.filter(v => (!next.style || v?.style === next.style) && (!next.color || v?.color === next.color)).map(v => v?.size).filter(Boolean));
        if (next.size && validSizes.size > 0 && !validSizes.has(next.size)) next.size = "";
      }

      return next;
    }));
  };

  const updatePOEditorQty = (editorId, nextQty) => {
    if (nextQty < 1) return;
    setPOEditorItems(prev => prev.map(row => row.editor_id === editorId ? { ...row, quantity: nextQty } : row));
  };

  const removePOEditorRow = (editorId) => {
    setPOEditorItems(prev => prev.filter(row => row.editor_id !== editorId));
  };

  const addPOEditorRow = () => {
    if (!editablePOProductId) return;
    setPOEditorItems(prev => [...prev, createDefaultPOEditorRow()]);
  };

  const getPOEditorRowPricing = (row) => {
    const product = products[row.product_id || editablePOProductId] || editablePOProduct;
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const matchedVariant = variants.find(v =>
      (!row.style || v?.style === row.style) &&
      (!row.color || v?.color === row.color) &&
      (!row.size || v?.size === row.size)
    );
    return matchedVariant?.pricing || product?.pricing || { final_price: product?.price || 0, base_price: product?.price || 0, savings: 0, size_price_adjustment: 0 };
  };

  const handleSavePOOrderEditor = async () => {
    if (poEditorItems.length === 0) {
      toast.error(locale === "id" ? "Minimal harus ada 1 item pesanan" : "At least one order item is required");
      return;
    }

    setSavingPOEditor(true);
    try {
      const response = await API.put(`/orders/${id}/items/revise`, {
        items: poEditorItems.map(row => ({
          transaction_detail_id: row.transaction_detail_id || undefined,
          quantity: row.quantity,
          item_notes: row.item_notes || "",
          variant: {
            ...(row.style ? { style: row.style } : {}),
            ...(row.color ? { color: row.color } : {}),
            ...(row.size ? { size: row.size } : {}),
            ...(getPOEditorRowPricing(row).size_price_adjustment ? { size_price_adjustment: getPOEditorRowPricing(row).size_price_adjustment } : {}),
          }
        }))
      });
      setOrder(response.data);
      setShowPOEditor(false);
      setPOEditorItems([]);
      toast.success(locale === "id" ? "Pesanan PO berhasil diperbarui" : "PO order updated successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || (locale === "id" ? "Gagal memperbarui pesanan PO" : "Failed to update PO order"));
    } finally {
      setSavingPOEditor(false);
    }
  };

  const handleDownloadSplitTemplate = () => {
    window.location.href = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/api/orders/${id}/split-template?token=${localStorage.getItem("token")}`;
  };

  const handleUploadSplit = async () => {
    if (!splitFile) return;
    setUploadingSplit(true);
    const formData = new FormData();
    formData.append("file", splitFile);
    try {
      await API.post(`/orders/${id}/split-upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Pengiriman berhasil dipecah!");
      setShowSplitModal(false);
      setSplitFile(null);
      const resOrder = await API.get(`/orders/${id}`);
      setOrder(resOrder.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal memproses excel. Pastikan format benar.");
    } finally {
      setUploadingSplit(false);
    }
  };

  // ----- Manual Split Logic -----
  const handleOpenSplitModal = () => {
    navigate(`/orders/${id}/shipping`);
  };

  const getLockedQty = (item_id) => {
    const item = order?.items?.find(i => i.transaction_detail_id === item_id);
    if (!item?.shipment_id) return 0;
    const linkedShipment = (order?.shipments || []).find(s => s.id === item.shipment_id);
    return linkedShipment?.awb ? (item.quantity || 0) : 0;
  };

  const getRemainingQty = (item_id) => {
    const total = order?.items?.find(i => i.transaction_detail_id === item_id)?.quantity || 0;
    const locked = getLockedQty(item_id);
    let allocated = 0;
    manualShipments.forEach(ship => {
      const found = ship.items.find(i => i.item_id === item_id);
      if (found) allocated += found.qty;
    });
    return Math.max(0, total - locked - allocated);
  };

  const handleAddManualShipment = () => {
    setManualShipments([
      ...manualShipments,
      { id: Date.now(), name: "", phone: "", address: "", isDropship: false, sender_name: "", sender_phone: "", sender_address: "", items: [] }
    ]);
  };

  const handleRemoveManualShipment = (shipId) => {
    setManualShipments(manualShipments.filter(s => s.id !== shipId));
  };

  const handleUpdateManualShipment = (shipId, field, value) => {
    setManualShipments(manualShipments.map(s => s.id === shipId ? { ...s, [field]: value } : s));
  };

  const handleIncrementManualItem = (shipId, item_id) => {
    const rem = getRemainingQty(item_id);
    if (rem <= 0) return toast.error("Kuantitas produk ini sudah habis dialokasikan.");
    setManualShipments(manualShipments.map(s => {
      if (s.id !== shipId) return s;
      const existing = s.items.find(i => i.item_id === item_id);
      let newItems = [...s.items];
      if (existing) {
        newItems = newItems.map(i => i.item_id === item_id ? { ...i, qty: i.qty + 1 } : i);
      } else {
        newItems.push({ item_id, qty: 1 });
      }
      return { ...s, items: newItems };
    }));
  };

  const handleDecrementManualItem = (shipId, item_id) => {
    setManualShipments(manualShipments.map(s => {
      if (s.id !== shipId) return s;
      const existing = s.items.find(i => i.item_id === item_id);
      if (!existing) return s; // shouldn't happen
      let newItems = [...s.items];
      if (existing.qty <= 1) {
        newItems = newItems.filter(i => i.item_id !== item_id); // remove if zero
      } else {
        newItems = newItems.map(i => i.item_id === item_id ? { ...i, qty: i.qty - 1 } : i);
      }
      return { ...s, items: newItems };
    }));
  };

  const handleSubmitManualSplit = async () => {
    if (manualShipments.length === 0) return toast.error("Tidak ada data penerima!");
    for (const s of manualShipments) {
      if (!s.name || !s.address) return toast.error("Nama dan Alamat wajib diisi untuk semua penerima.");
      if (s.isDropship && (!s.sender_name || !s.sender_phone)) return toast.error(`Nama dan No HP pengirim dropship wajib diisi untuk penerima ${s.name}.`);
      if (s.items.length === 0) return toast.error(`Penerima ${s.name} tidak memiliki keranjang barang.`);
    }

    const payload = manualShipments.map(s => ({
      ...s,
      sender_name: s.isDropship ? s.sender_name : "",
      sender_phone: s.isDropship ? s.sender_phone : "",
      sender_address: s.isDropship ? s.sender_address : ""
    }));

    setUploadingSplit(true);
    try {
      await API.post(`/orders/${id}/split-manual`, { shipments: payload });
      toast.success("Pengiriman berhasil dipecah secara manual!");
      setShowSplitModal(false);
      setManualShipments([]);
      const resOrder = await API.get(`/orders/${id}`);
      setOrder(resOrder.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal memproses split. Periksa kembali form anda.");
    } finally {
      setUploadingSplit(false);
    }
  };


  if (loading) return <PageWrapper isLoading={true} />;

  if (!order) {
    return (
      <PageWrapper>
        <div className="text-center py-20 bg-white border border-stone-200 rounded-xl">
          <Package className="w-12 h-12 text-stone-200 mx-auto mb-4" />
          <p className="text-stone-500">{t("orders.not_found") || "Pesanan tidak ditemukan."}</p>
        </div>
      </PageWrapper>
    );
  }

  const paymentSummary = order.payment_summary || {};

  // Calculate Order Type & DP
  const isPO = paymentSummary.is_preorder ?? (order.items || []).some(item => products[item.product_id]?.stock_type === "preorder");
  let dpRequired = paymentSummary.dp_target ?? 0;

  if (isPO && !dpRequired) {
    // DP is calculated per item based on product's dp_percentage
    dpRequired = order.items.reduce((sum, item) => {
      const p = products[item.product_id];
      const pct = p ? (p.dp_percentage || 50) : 50;
      return sum + (item.item_total * (pct / 100));
    }, 0);
  }

  const grandTotal = order.grand_total || order.total_price;
  const grandTotalOriginal = grandTotal + Number(order.total_discount || 0);
  const totalDiscount = Number(order.total_discount || 0);
  const totalItemQty = (order.items || []).reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  const totalPaid = paymentSummary.total_paid ?? installments.reduce((sum, inst) => sum + inst.nominal, 0);
  const remaining = paymentSummary.outstanding_total ?? Math.max(0, grandTotal - totalPaid);
  const dpAmount = paymentSummary.dp_target ?? dpRequired;
  const dpPercent = grandTotal > 0 ? Math.round((dpAmount / grandTotal) * 100) : 0;
  const settlementAmount = paymentSummary.settlement_due ?? Math.max(0, grandTotal - dpAmount);

  // Shipping verification
  let shippingData = {};
  if (order.shipping && typeof order.shipping === "object") {
    shippingData = order.shipping;
  } else if (order.shipping && typeof order.shipping === "string") {
    try { shippingData = JSON.parse(order.shipping); } catch { }
  }
  const recipientInfo = (order.recipient_info && typeof order.recipient_info === "object") ? order.recipient_info : shippingData;
  const checkValidAddr = (str) => typeof str === "string" && str.trim() !== "" && str.trim() !== "-";
  const hasAddress = checkValidAddr(recipientInfo?.address) || checkValidAddr(shippingData.address);

  const isDPLunas = totalPaid >= dpRequired;
  const isLunasAll = totalPaid >= grandTotal;
  const dpPaidAmount = isPO ? Math.min(totalPaid, dpRequired) : 0;
  const dpRemaining = isPO ? Math.max(0, paymentSummary.dp_due ?? (dpRequired - dpPaidAmount)) : 0;
  const isPartialDPPaid = isPO && dpPaidAmount > 0 && dpRemaining > 0 && !isDPLunas;

  const hasPendingDP = confirmations.some(c => c.payment_type === "dp" && c.status === "pending");
  const hasPendingSettlement = confirmations.some(c => c.payment_type === "settlement" && c.status === "pending");
  const hasPendingFull = confirmations.some(c => c.payment_type === "full" && c.status === "pending");
  const isWaitingVerificationDP = order.payment_status_v2 === "waiting_verification_dp" || hasPendingDP;
  const isWaitingVerificationSettlement = order.payment_status_v2 === "waiting_verification_settlement" || hasPendingSettlement;
  const isWaitingVerificationFull = hasPendingFull;
  const currentUploadPaymentType = isPO ? (!isDPLunas ? "dp" : "settlement") : "full";
  const hasPendingCurrentPayment = currentUploadPaymentType === "dp" ? isWaitingVerificationDP : currentUploadPaymentType === "settlement" ? isWaitingVerificationSettlement : isWaitingVerificationFull;
  const pendingPaymentLabel = currentUploadPaymentType === "dp" ? "Bukti DP sedang direview admin" : currentUploadPaymentType === "settlement" ? "Bukti pelunasan sedang direview admin" : "Bukti pembayaran sedang direview admin";
  const paymentStatusMap = isPO ? {
    unpaid: { label: "Belum Bayar", color: "bg-amber-100 text-amber-800" },
    partially_paid: { label: "Bayar Sebagian", color: "bg-blue-100 text-blue-800" },
    waiting_verification_dp: { label: "Menunggu Verifikasi DP", color: "bg-blue-600 text-white shadow-sm ring-2 ring-blue-100 animate-pulse-subtle" },
    dp_paid: { label: "Sudah DP", color: "bg-sky-100 text-sky-800" },
    waiting_verification_settlement: { label: "Menunggu Verifikasi Pelunasan", color: "bg-blue-600 text-white shadow-sm ring-2 ring-blue-100 animate-pulse-subtle" },
    paid: { label: "Lunas", color: "bg-emerald-100 text-emerald-800" },
    cancelled: { label: "Dibatalkan", color: "bg-red-100 text-red-800" },
  } : {
    unpaid: { label: "Menunggu Pembayaran", color: "bg-amber-100 text-amber-800" },
    partially_paid: { label: "Bayar Sebagian", color: "bg-blue-100 text-blue-800" },
    paid: { label: "Lunas", color: "bg-emerald-100 text-emerald-800" },
    cancelled: { label: "Dibatalkan", color: "bg-red-100 text-red-800" },
  };
  const orderProgressMap = {
    pending: { label: isPO ? "Menunggu Diproses" : "Menunggu Pembayaran", color: "bg-stone-100 text-stone-700" },
    waiting_po_close: { label: "Menunggu PO Close", color: "bg-yellow-100 text-yellow-800" },
    po_closed: { label: "PO Ditutup", color: "bg-orange-100 text-orange-800" },
    sent_to_central: { label: "Dikirim ke Pusat", color: "bg-cyan-100 text-cyan-800" },
    in_production: { label: "Dalam Produksi", color: "bg-indigo-100 text-indigo-800" },
    ready_at_central: { label: "Ready di Pusat", color: "bg-violet-100 text-violet-800" },
    ready_at_gallery: { label: "Ready di Gallery", color: "bg-fuchsia-100 text-fuchsia-800" },
    ready_to_ship: { label: "Siap Kirim", color: "bg-cyan-100 text-cyan-800" },
    shipped: { label: "Dikirim", color: "bg-purple-100 text-purple-800" },
    completed: { label: "Selesai", color: "bg-emerald-600 text-white" },
    cancelled: { label: "Dibatalkan", color: "bg-red-100 text-red-800" },
  };
  const paymentStatusMeta = paymentStatusMap[order.payment_status_v2] || null;
  const orderProgressMeta = orderProgressMap[order.order_status_v2] || null;
  const primaryStatusMeta = (() => {
    if (isWaitingVerificationDP) return paymentStatusMap.waiting_verification_dp;
    if (isWaitingVerificationSettlement) return paymentStatusMap.waiting_verification_settlement;
    if (isWaitingVerificationFull) return { label: t("order_detail.waiting_verification") || "Menunggu Verifikasi Pembayaran", color: "bg-blue-600 text-white shadow-sm ring-2 ring-blue-100 animate-pulse-subtle" };
    if (orderProgressMeta) return orderProgressMeta;
    if (paymentStatusMeta) return paymentStatusMeta;
    const def = orderStatuses.find(s => s.code === order.status);
    return {
      label: def ? (locale === "id" ? def.name_id : def.name_en) : (order.status || "-") ,
      color: def?.color || "bg-stone-100 text-stone-600"
    };
  })();
  const hasConfiguredShipments = Array.isArray(order.shipments) && order.shipments.length > 0;
  const hasAnyShipmentAwb = hasConfiguredShipments && order.shipments.some((s) => !!s.awb);
  const shipmentMap = new Map((order.shipments || []).map((s) => [s.id, s]));
  const allItemsAllocatedToShipments = (order.items || []).length > 0 && (order.items || []).every((item) => item.shipment_id && shipmentMap.has(item.shipment_id));
  const isShippingSetupComplete = hasConfiguredShipments && allItemsAllocatedToShipments && (order.shipments || []).every((s) =>
    !!String(s.name || '').trim() &&
    !!String(s.phone || '').trim() &&
    !!String(s.address || '').trim() &&
    !!String(s.destination_id || '').trim() &&
    !!String(s.courier || '').trim() &&
    !!String(s.shipping_service || '').trim() &&
    Number(s.shipping_cost || 0) > 0
  );
  const canPrepareShippingStatus = ["in_production", "ready_at_central", "ready_at_gallery", "ready_to_ship", "shipped"].includes(order.order_status_v2);
  const canEditShippingSetup = isPO && canPrepareShippingStatus && !hasAnyShipmentAwb && order.payment_status_v2 !== "cancelled" && order.order_status_v2 !== "completed";
  const shouldLockSettlementForShipping = canEditShippingSetup && isDPLunas && remaining > 0 && !isShippingSetupComplete;
  const shouldPromptShippingSetup = canEditShippingSetup && !isShippingSetupComplete;
  const shouldShowProductionNotice = isPO && ["waiting_po_close", "po_closed", "sent_to_central", "in_production"].includes(order.order_status_v2) && !hasConfiguredShipments;
  const shouldShowAwaitingAwbNotice = isPO && hasConfiguredShipments && isShippingSetupComplete && !hasAnyShipmentAwb;
  const productionNoticeCopy = order.order_status_v2 === "sent_to_central"
    ? {
        title: "Pesanan sudah dikirim ke pusat",
        desc: "Admin sudah mengirim batch PO ke pusat. Langkah berikutnya tinggal menunggu barang masuk tahap produksi sebelum pengiriman bisa diatur."
      }
    : order.order_status_v2 === "in_production"
      ? {
          title: "Pesanan masih dalam produksi",
          desc: "Barang masih diproses oleh pusat. Setelah status berubah ke Ready di Pusat atau Ready di Gallery, kamu bisa lanjut klik Atur Pengiriman."
        }
      : {
          title: "Pengiriman belum perlu diatur dulu",
          desc: "Pesanan masih menunggu PO close. Nanti setelah status berubah ke Ready di Pusat atau Ready di Gallery, kamu bisa lanjut klik Atur Pengiriman."
        };

  const currentStatusClass = primaryStatusMeta.color;

  const paymentTimelineSteps = isPO
    ? [
      { title: "Pesanan Dibuat", done: true, active: false, desc: new Date(order.created_at).toLocaleString() },
      {
        title: "Pembayaran DP",
        active: (!isDPLunas || isWaitingVerificationDP) && !isLunasAll && order.status !== "cancelled",
        done: isDPLunas && !isWaitingVerificationDP,
        desc: isWaitingVerificationDP ? "Bukti bayar DP sedang direview admin" : (isDPLunas ? "DP terpenuhi" : `Tagihan DP: ${formatPrice(paymentSummary.dp_due ?? dpRequired)}`)
      },
      {
        title: "Pelunasan",
        active: (isDPLunas || isWaitingVerificationSettlement) && !isLunasAll && order.status !== "cancelled",
        done: isLunasAll && !isWaitingVerificationSettlement,
        desc: isWaitingVerificationSettlement ? "Bukti pelunasan sedang direview admin" : (isLunasAll ? "Lunas" : `Sisa pelunasan: ${formatPrice(paymentSummary.settlement_due ?? remaining)}`)
      },
    ]
    : [
      { title: "Pesanan Dibuat", done: true, active: false, desc: new Date(order.created_at).toLocaleString() },
      {
        title: "Pembayaran",
        active: !isLunasAll && order.status !== "cancelled",
        done: isLunasAll,
        desc: isWaitingVerificationFull ? (t("order_detail.waiting_verification") || "Menunggu Verifikasi Pembayaran") : (isLunasAll ? "Lunas" : `Sisa tagihan: ${formatPrice(remaining || grandTotal)}`)
      },
    ];

  const orderProgressSteps = isPO
    ? [
      {
        title: order.order_status_v2 === "po_closed" ? "PO Ditutup" : "Menunggu PO Close",
        active: ["waiting_po_close", "po_closed"].includes(order.order_status_v2),
        done: ["sent_to_central", "in_production", "ready_at_central", "ready_at_gallery", "ready_to_ship", "shipped", "completed"].includes(order.order_status_v2),
        desc: "Order tercatat dan menunggu batch PO ditutup"
      },
      {
        title: "Dikirim ke Pusat",
        active: order.order_status_v2 === "sent_to_central",
        done: ["in_production", "ready_at_central", "ready_at_gallery", "ready_to_ship", "shipped", "completed"].includes(order.order_status_v2),
        desc: "Admin sudah mengirim data PO ke pusat"
      },
      {
        title: "Dalam Produksi",
        active: order.order_status_v2 === "in_production",
        done: ["ready_at_central", "ready_at_gallery", "ready_to_ship", "shipped", "completed"].includes(order.order_status_v2),
        desc: "Barang sedang diproses oleh pusat"
      },
      {
        title: "Ready Barang",
        active: ["ready_at_central", "ready_at_gallery"].includes(order.order_status_v2),
        done: ["ready_to_ship", "shipped", "completed"].includes(order.order_status_v2),
        desc: order.order_status_v2 === "ready_at_gallery" ? "Barang sudah ready di gallery" : "Barang sudah ready di pusat"
      },
      {
        title: "Siap Kirim",
        active: order.order_status_v2 === "ready_to_ship",
        done: ["shipped", "completed"].includes(order.order_status_v2),
        desc: hasAddress ? "Alamat penerima sudah siap diproses" : "Menunggu alamat / finalisasi pengiriman"
      },
      {
        title: "Dikirim",
        active: order.order_status_v2 === "shipped",
        done: order.order_status_v2 === "completed",
        desc: "Pesanan sedang dalam perjalanan"
      },
      {
        title: "Selesai",
        active: order.order_status_v2 === "completed",
        done: order.order_status_v2 === "completed",
        desc: "Pesanan selesai"
      },
    ]
    : [
      {
        title: "Menunggu Pembayaran",
        active: ["pending"].includes(order.order_status_v2 || "pending"),
        done: ["processing", "shipped", "completed"].includes(order.order_status_v2),
        desc: isLunasAll ? "Pembayaran sudah dikonfirmasi" : "Selesaikan pembayaran agar pesanan diproses"
      },
      {
        title: "Diproses",
        active: order.order_status_v2 === "processing",
        done: ["shipped", "completed"].includes(order.order_status_v2),
        desc: "Pesanan sedang disiapkan"
      },
      {
        title: "Dikirim",
        active: order.order_status_v2 === "shipped",
        done: order.order_status_v2 === "completed",
        desc: "Paket di jasa kirim"
      },
      {
        title: "Selesai",
        active: order.order_status_v2 === "completed",
        done: order.order_status_v2 === "completed",
        desc: "Pesanan selesai"
      },
    ];

  return (
    <PageWrapper>
      <div className="flex flex-col lg:flex-row gap-8 items-start">

        {/* Left Column (Tracking & Items) */}
        <div className="flex-1 space-y-6 w-full">

          {/* Header Status */}
          <div className="bg-white border border-stone-200 rounded-xl p-6 shadow-sm">
            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4 mb-4">
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="font-serif text-xl sm:text-2xl text-stone-900 leading-tight">
                    {t("orders.order_id") || "Order"} <span className="font-sans text-sm sm:text-base font-semibold tracking-wide text-stone-700 break-all">{order.id}</span>
                  </h1>
                  <button
                    onClick={() => { navigator.clipboard.writeText(order.id); toast.success("Order ID copied!"); }}
                    className="p-1 hover:bg-stone-100 rounded text-stone-400 transition-colors"
                    title="Copy Order ID"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
                <p className="text-sm text-stone-500 mt-1 flex items-center gap-2">
                  {new Date(order.created_at).toLocaleDateString(locale === "id" ? "id-ID" : "en-US", { year: "numeric", month: "long", day: "numeric" })}
                  <Badge className="bg-stone-50 text-stone-600 font-normal border-stone-200">{isPO ? t("home.preorder") : t("home.ready_stock")}</Badge>
                </p>
              </div>
              <div className="w-full sm:w-auto">
                <div className="flex flex-col items-stretch sm:items-end gap-2">
                  <Badge className={`w-full sm:w-auto justify-center px-4 py-2 uppercase tracking-wider text-xs font-bold rounded-lg ${currentStatusClass}`}>
                    {primaryStatusMeta.label}
                  </Badge>
                  {(paymentStatusMeta || orderProgressMeta) && (
                    <div className="grid grid-cols-1 xs:grid-cols-2 sm:flex sm:flex-wrap sm:justify-end gap-2 w-full sm:w-auto">
                      {paymentStatusMeta && (
                        <div className={`rounded-lg px-3 py-2 text-center sm:text-left ${paymentStatusMeta.color}`}>
                          <p className="text-[9px] uppercase tracking-wider opacity-70 font-bold leading-none mb-1">Status Bayar</p>
                          <p className="text-[11px] uppercase font-bold leading-tight">{paymentStatusMeta.label}</p>
                        </div>
                      )}
                      {orderProgressMeta && (
                        <div className={`rounded-lg px-3 py-2 text-center sm:text-left ${orderProgressMeta.color}`}>
                          <p className="text-[9px] uppercase tracking-wider opacity-70 font-bold leading-none mb-1">Status Pesanan</p>
                          <p className="text-[11px] uppercase font-bold leading-tight">{orderProgressMeta.label}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {order.payment_summary && (
              <div className="grid grid-cols-2 gap-3 pt-4 border-t border-stone-100">
                <div className="rounded-lg border border-emerald-100 bg-emerald-50/70 p-3">
                  <p className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold">Sudah Dibayar</p>
                  <p className="text-base font-semibold text-emerald-800 mt-1">{formatPrice(totalPaid)}</p>
                </div>
                <div className="rounded-lg border border-amber-100 bg-amber-50/70 p-3">
                  <p className="text-[11px] uppercase tracking-wider text-amber-700 font-semibold">Sisa Tagihan</p>
                  <p className="text-base font-semibold text-amber-800 mt-1">{formatPrice(remaining)}</p>
                </div>
              </div>
            )}
          </div>

          {isPO && (shouldShowProductionNotice || shouldPromptShippingSetup || shouldShowAwaitingAwbNotice) && (
            <div className="bg-white border border-stone-200 rounded-xl p-5 shadow-sm">
              {shouldShowProductionNotice && (
                <div className="flex items-start gap-3 bg-stone-50 border border-stone-200 rounded-lg p-4">
                  <div className="w-2.5 h-2.5 rounded-full bg-stone-400 mt-1.5 shrink-0"></div>
                  <div>
                    <p className="text-sm font-semibold text-stone-900">{productionNoticeCopy.title}</p>
                    <p className="text-xs text-stone-600 mt-1">{productionNoticeCopy.desc}</p>
                  </div>
                </div>
              )}
              {shouldPromptShippingSetup && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-cyan-50 border border-cyan-200 rounded-lg p-4">
                  <div>
                    <p className="text-sm font-semibold text-cyan-900">Pesanan siap diatur pengirimannya</p>
                    <p className="text-xs text-cyan-800 mt-1">Silakan buka halaman <span className="font-semibold">Atur Pengiriman</span> untuk lengkapi penerima, alamat, dan ongkir sebelum admin input AWB.</p>
                  </div>
                  <button onClick={handleOpenSplitModal} className="px-3 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-xs font-bold uppercase tracking-wider shadow-sm">
                    Atur Pengiriman
                  </button>
                </div>
              )}
              {shouldShowAwaitingAwbNotice && (
                <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-400 mt-1.5 shrink-0 animate-pulse"></div>
                  <div>
                    <p className="text-sm font-semibold text-amber-900">Pengiriman sudah diatur, menunggu input AWB</p>
                    <p className="text-xs text-amber-800 mt-1">Data penerima / dropshipper sudah tersimpan. Langkah berikutnya admin akan mengisi kurir dan nomor resi saat paket dikirim.</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tracking Progress Timeline */}
          <div className="bg-white border border-stone-200 rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-serif text-stone-900 mb-6">{t("order_detail.status_title")}</h2>
            {order.payment_summary && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                <div className="rounded-lg border border-stone-200 bg-stone-50 p-3">
                  <p className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Diupdate Oleh</p>
                  <p className="text-sm font-semibold text-stone-800 mt-1">{order.order_status_v2_source === "manual" ? "Manual Admin" : "Otomatis Sistem"}</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <div className="rounded-xl border border-stone-200 p-4 bg-stone-50/60">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-4">Timeline Pembayaran</h3>
                <div className="relative pl-6 border-l-2 border-stone-100 space-y-6 py-2">
                  {paymentTimelineSteps.map((step, idx) => (
                    <div key={`payment-${idx}`} className="relative">
                      <div className={`absolute -left-[35px] w-6 h-6 rounded-full border-2 flex items-center justify-center bg-white ${step.done ? 'border-emerald-500' : step.active ? 'border-primary ring-4 ring-primary/20' : 'border-stone-200'}`}>
                        {step.done && <CheckCircle className="w-4 h-4 text-emerald-500" />}
                        {!step.done && step.active && <div className="w-2 h-2 bg-primary rounded-full" />}
                      </div>
                      <div>
                        <h4 className={`font-semibold text-sm ${step.done ? 'text-stone-900' : step.active ? 'text-primary' : 'text-stone-400'}`}>{step.title}</h4>
                        <p className={`text-xs mt-1 ${step.active ? 'text-stone-600 font-medium' : 'text-stone-400'}`}>{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-stone-200 p-4 bg-stone-50/60">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-4">Timeline Pesanan</h3>
                <div className="relative pl-6 border-l-2 border-stone-100 space-y-6 py-2">
                  {orderProgressSteps.map((step, idx) => (
                    <div key={`order-${idx}`} className="relative">
                      <div className={`absolute -left-[35px] w-6 h-6 rounded-full border-2 flex items-center justify-center bg-white ${step.done ? 'border-emerald-500' : step.active ? 'border-primary ring-4 ring-primary/20' : 'border-stone-200'}`}>
                        {step.done && <CheckCircle className="w-4 h-4 text-emerald-500" />}
                        {!step.done && step.active && <div className="w-2 h-2 bg-primary rounded-full" />}
                      </div>
                      <div>
                        <h4 className={`font-semibold text-sm ${step.done ? 'text-stone-900' : step.active ? 'text-primary' : 'text-stone-400'}`}>{step.title}</h4>
                        <p className={`text-xs mt-1 ${step.active ? 'text-stone-600 font-medium' : 'text-stone-400'}`}>{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div className="bg-white border border-stone-200 rounded-xl p-6 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <h2 className="text-lg font-serif text-stone-900">{t("order_detail.items_title")}</h2>
              {order.can_customer_edit_po_items && (
                <button onClick={() => showPOEditor ? closePOOrderEditor() : openPOOrderEditor()} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-bold uppercase tracking-wider hover:opacity-90 transition-colors">
                  {showPOEditor ? "Tutup Editor" : "Edit Pesanan PO"}
                </button>
              )}
            </div>

            {order.can_customer_edit_po_items && (
              <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-amber-700">Edit pesanan masih dibuka</p>
                <p className="text-xs text-amber-800 mt-1">Selama DP belum dibayar, belum ada bukti bayar yang sedang direview, dan PO belum close, customer masih bisa tambah/kurang qty, ubah varian, hapus baris, atau tambah varian baru untuk produk PO yang sama. Harga akan mengikuti harga live saat edit.</p>
              </div>
            )}

            {!order.can_customer_edit_po_items && order.has_pending_confirmation && (
              <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-blue-700">Edit pesanan dikunci sementara</p>
                <p className="text-xs text-blue-800 mt-1">Bukti pembayaran sedang menunggu approval admin. Selama proses review, item PO tidak bisa diubah. Jika bukti bayar direject, edit akan otomatis dibuka lagi. Jika diapprove, pesanan tetap terkunci.</p>
              </div>
            )}

            {showPOEditor && order.can_customer_edit_po_items && (
              <div className="mb-6 rounded-xl border border-primary/20 bg-primary/[0.05] p-4 space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-primary">Editor Pesanan PO</p>
                    <p className="text-sm text-stone-600 mt-1">Satu transaksi ini hanya untuk satu produk PO, jadi kamu bisa atur kombinasi varian dan jumlahnya di sini.</p>
                  </div>
                  {editablePOProduct?.images?.[0] && (
                    <img src={getImageUrl(editablePOProduct.images[0])} alt={editablePOProduct?.name || "PO Product"} className="w-14 h-16 rounded-lg object-cover border border-stone-200" />
                  )}
                </div>

                <div className="space-y-3">
                  {poEditorItems.map((row) => {
                    const rowOptions = getPOEditorVariantOptions(row);
                    const rowPricing = getPOEditorRowPricing(row);
                    const rowTotal = (rowPricing.final_price || 0) * row.quantity;
                    return (
                      <div key={row.editor_id} className="rounded-lg border border-stone-200 bg-white p-4 space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          {rowOptions.styles.length > 0 && (
                            <label className="text-xs text-stone-600 space-y-1 block">
                              <span className="font-semibold uppercase tracking-wider text-stone-500">Varian</span>
                              <select value={row.style || ""} onChange={(e) => updatePOEditorField(row.editor_id, "style", e.target.value)} className="w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:border-primary">
                                <option value="">Pilih Varian</option>
                                {rowOptions.styles.map(style => <option key={`${row.editor_id}-${style}`} value={style}>{style}</option>)}
                              </select>
                            </label>
                          )}
                          {rowOptions.colors.length > 0 && (
                            <label className="text-xs text-stone-600 space-y-1 block">
                              <span className="font-semibold uppercase tracking-wider text-stone-500">Warna</span>
                              <select value={row.color || ""} onChange={(e) => updatePOEditorField(row.editor_id, "color", e.target.value)} className="w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:border-primary">
                                <option value="">Pilih Warna</option>
                                {rowOptions.colors.map(color => <option key={`${row.editor_id}-${color}`} value={color}>{color}</option>)}
                              </select>
                            </label>
                          )}
                          {rowOptions.sizes.length > 0 && (
                            <label className="text-xs text-stone-600 space-y-1 block">
                              <span className="font-semibold uppercase tracking-wider text-stone-500">Ukuran</span>
                              <select value={row.size || ""} onChange={(e) => updatePOEditorField(row.editor_id, "size", e.target.value)} className="w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:border-primary">
                                <option value="">Pilih Ukuran</option>
                                {rowOptions.sizes.map(size => <option key={`${row.editor_id}-${size}`} value={size}>{size}</option>)}
                              </select>
                            </label>
                          )}
                        </div>

                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center border border-stone-200 rounded overflow-hidden bg-white">
                              <button type="button" onClick={() => updatePOEditorQty(row.editor_id, row.quantity - 1)} className="w-9 h-9 flex items-center justify-center text-stone-500 hover:bg-stone-50"><Minus className="w-3.5 h-3.5" /></button>
                              <span className="w-10 h-9 flex items-center justify-center text-sm font-semibold text-stone-800 border-x border-stone-200">{row.quantity}</span>
                              <button type="button" onClick={() => updatePOEditorQty(row.editor_id, row.quantity + 1)} className="w-9 h-9 flex items-center justify-center text-stone-500 hover:bg-stone-50"><Plus className="w-3.5 h-3.5" /></button>
                            </div>
                            <div className="text-xs text-stone-500 space-y-1">
                              <p>Harga live: <span className="font-semibold text-stone-700">{formatPrice(rowPricing.final_price || 0)}</span></p>
                              {Number(rowPricing.size_price_adjustment || 0) > 0 && (
                                <p>Tambahan size: <span className="font-semibold text-stone-700">+{formatPrice(rowPricing.size_price_adjustment)}</span></p>
                              )}
                              <p>Subtotal: <span className="font-semibold text-stone-900">{formatPrice(rowTotal)}</span></p>
                            </div>
                          </div>
                          <button type="button" onClick={() => removePOEditorRow(row.editor_id)} disabled={poEditorItems.length <= 1} className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                            <Trash2 className="w-4 h-4" /> Hapus
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-stone-200 pt-4">
                  <button type="button" onClick={addPOEditorRow} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-stone-200 text-stone-700 hover:bg-white transition-colors text-sm font-semibold">
                    <Plus className="w-4 h-4" /> Tambah Varian
                  </button>
                  <div className="text-left sm:text-right">
                    <p className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Estimasi Total Baru</p>
                    <p className="text-lg font-bold text-stone-900">{formatPrice(poEditorItems.reduce((acc, row) => acc + ((getPOEditorRowPricing(row).final_price || 0) * row.quantity), 0))}</p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                  <button type="button" onClick={closePOOrderEditor} className="px-4 py-2 rounded-lg bg-stone-100 text-stone-600 text-sm font-semibold hover:bg-stone-200 transition-colors">
                    Batal
                  </button>
                  <button type="button" onClick={handleSavePOOrderEditor} disabled={savingPOEditor} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-colors disabled:opacity-50">
                    {savingPOEditor ? "Menyimpan..." : "Simpan Perubahan Pesanan"}
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-4">
              {order.items?.map((item, i) => (
                <div key={i} className="p-4 border border-stone-200 bg-white rounded-2xl shadow-sm space-y-3">
                  <div className="flex gap-4">
                    <div className="w-16 h-20 bg-stone-100 rounded-xl overflow-hidden shadow-sm shrink-0 border border-stone-100">
                      {products[item.product_id]?.images?.[0] ? (
                        <img src={getImageUrl(products[item.product_id].images[0])} alt={item.product_name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-stone-400"><Package className="w-6 h-6" /></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-semibold text-stone-900 text-sm leading-snug">
                            {getDisplayProductLabel(item.product_name, item.brand_name)}
                          </p>
                          <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-wide font-semibold">
                            {getDisplayVariantValues(item.variant).length > 0 ? getDisplayVariantValues(item.variant).map((value, idx) => (
                              <span key={idx} className="inline-flex items-center gap-1 rounded-full bg-stone-100 text-stone-600 px-2.5 py-1 border border-stone-200">
                                {String(value)}
                              </span>
                            )) : (
                              <span className="inline-flex items-center gap-1 rounded-full bg-stone-100 text-stone-500 px-2.5 py-1 border border-stone-200">Varian Standar</span>
                            )}
                            <span className="inline-flex items-center rounded-full bg-stone-100 text-stone-500 px-2.5 py-1 border border-stone-200 normal-case">× {item.quantity} item</span>
                          </div>
                          {Number(item.variant?.size_price_adjustment || 0) > 0 && (
                            <p className="text-[11px] text-stone-500 mt-2">Tambahan size: <span className="font-semibold">+{formatPrice(item.variant.size_price_adjustment)}</span></p>
                          )}
                          {item.item_notes && <p className="text-[11px] text-stone-400 italic mt-2">Catatan: {item.item_notes}</p>}
                        </div>
                        <div className="text-left sm:text-right shrink-0">
                          {Number(item.discount_snapshot || 0) > 0 ? (
                            <>
                              <p className="text-[11px] text-stone-400 line-through">{formatPrice((Number(item.price_snapshot || item.price || 0) + Number(item.discount_snapshot || 0)) * Number(item.quantity || 1))}</p>
                              <p className="text-sm font-semibold text-stone-900">{formatPrice(item.item_total)}</p>
                            </>
                          ) : (
                            <p className="text-sm font-semibold text-stone-900">{formatPrice(item.item_total)}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* Right Column (Payment & Actions) */}
        <div className="w-full lg:w-[380px] space-y-6 shrink-0">

          {/* Tagihan Box */}
          <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm">
            <h2 className="text-lg font-serif text-stone-900 mb-4">Rincian Pembayaran</h2>
            <div className="space-y-3 text-sm">
              <div className="flex items-start justify-between gap-4">
                <span className="text-stone-600">Total Harga ({totalItemQty} item)</span>
                <div className="text-right">
                  {totalDiscount > 0 ? (
                    <>
                      <p className="text-[11px] text-stone-400 line-through">{formatPrice(grandTotalOriginal)}</p>
                      <p className="font-semibold text-stone-900">{formatPrice(grandTotal)}</p>
                    </>
                  ) : (
                    <p className="font-semibold text-stone-900">{formatPrice(grandTotal)}</p>
                  )}
                </div>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-stone-500">Subtotal</span>
                <span className="text-stone-700">{formatPrice(grandTotalOriginal)}</span>
              </div>
              {totalDiscount > 0 && (
                <div className="flex justify-between gap-4">
                  <span className="text-stone-500">Diskon Keanggotaan</span>
                  <span className="text-rose-600">-{formatPrice(totalDiscount)}</span>
                </div>
              )}
              {isPO && (
                <>
                  <div className="flex justify-between gap-4">
                    <span className="text-stone-500">Target DP ({dpPercent}%)</span>
                    <span className="font-semibold text-stone-900">{formatPrice(dpAmount)}</span>
                  </div>
                  {(dpPaidAmount > 0 || isPartialDPPaid) && (
                    <div className="flex justify-between gap-4">
                      <span className="text-stone-500">DP sudah dibayar</span>
                      <span className="font-semibold text-emerald-700">{formatPrice(dpPaidAmount)}</span>
                    </div>
                  )}
                  {isPartialDPPaid && (
                    <div className="flex justify-between gap-4">
                      <span className="text-stone-500">Sisa DP</span>
                      <span className="font-semibold text-orange-700">{formatPrice(dpRemaining)}</span>
                    </div>
                  )}
                  <div className="flex justify-between gap-4">
                    <span className="text-stone-500">Pelunasan ({Math.max(0, 100 - dpPercent)}%)</span>
                    <span className="font-semibold text-stone-900">{formatPrice(settlementAmount)}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between gap-4">
                <span className="text-stone-500">Ongkos Kirim</span>
                <div className="text-right">
                  <p className="font-semibold text-stone-900">{formatPrice(Number(shippingData.shipping_cost || 0))}</p>
                  {(shippingData.service_name || shippingData.courier_name || shippingData.courier) && (
                    <p className="text-[11px] text-stone-400">{shippingData.courier_name || shippingData.courier} {shippingData.service_name ? `· ${shippingData.service_name}` : ""}</p>
                  )}
                </div>
              </div>
              <div className="pt-3 border-t border-stone-100 flex justify-between gap-4">
                <span className="text-stone-600 font-medium">Sudah dibayar</span>
                <span className="font-semibold text-stone-900">{formatPrice(totalPaid)}</span>
              </div>
              {remaining > 0 && (
                <div className="flex justify-between gap-4">
                  <span className="text-stone-600 font-medium">Sisa tagihan</span>
                  <span className="font-semibold text-amber-700">{formatPrice(remaining)}</span>
                </div>
              )}
            </div>
          </div>            {/* Aksi Pembayaran Baru */}
          {order.status !== "cancelled" && remaining > 0 && (
            <div className="bg-white border border-stone-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4 text-primary">
                <CreditCard className="w-5 h-5" />
                <h3 className="font-serif text-lg text-stone-900">{t("order_detail.payment_title")}</h3>
              </div>

              {isPO && !isDPLunas ? (
                <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg mb-4">
                  <p className="text-sm text-orange-800 font-medium mb-1">{isPartialDPPaid ? "DP belum terpenuhi" : t("order_detail.po_notice")}</p>
                  <p className="text-2xl font-bold text-orange-600 mb-2">{formatPrice(dpRemaining || dpRequired)}</p>
                  {isPartialDPPaid && (
                    <div className="rounded-lg bg-white/70 border border-orange-100 p-3 mb-3 space-y-1 text-xs">
                      <div className="flex justify-between gap-3">
                        <span className="text-orange-700">Target DP</span>
                        <span className="font-semibold text-orange-900">{formatPrice(dpRequired)}</span>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span className="text-orange-700">Sudah dibayar</span>
                        <span className="font-semibold text-emerald-700">{formatPrice(dpPaidAmount)}</span>
                      </div>
                      <div className="flex justify-between gap-3 pt-1 border-t border-orange-100">
                        <span className="text-orange-800 font-medium">Sisa DP</span>
                        <span className="font-bold text-orange-700">{formatPrice(dpRemaining)}</span>
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-orange-700">{isPartialDPPaid ? "Lanjutkan pembayaran sisa DP agar pesanan bisa masuk tahap berikutnya." : (t("orders.upload_desc") || "Transfer ke bank dan kirim konfirmasi pembayaran.")}</p>
                </div>
              ) : shouldLockSettlementForShipping ? (
                <div className="bg-cyan-50 border border-cyan-200 p-4 rounded-lg mb-4">
                  <p className="text-sm text-cyan-900 font-semibold mb-1">Atur pengiriman dulu sebelum pelunasan</p>
                  <p className="text-xs text-cyan-800 mb-3">Ongkir harus dihitung dan tersimpan supaya total pelunasan final tidak kurang bayar.</p>
                  <Link to={`/orders/${order.id}/shipping`} className="inline-flex items-center gap-2 px-3 py-2 bg-cyan-700 text-white rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-cyan-800 transition-colors">
                    <SplitSquareVertical className="w-4 h-4" /> Atur Pengiriman
                  </Link>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg mb-4">
                  <p className="text-sm text-amber-800 font-medium mb-1">{isPO ? t("order_detail.confirm_settlement") : t("order_detail.rs_notice")}</p>
                  <p className="text-2xl font-bold text-amber-600 mb-2">{formatPrice(remaining)}</p>
                  {order.payment_due_date && (
                    <p className="text-xs text-amber-700 font-medium">{t("order_detail.due_date")}: {new Date(order.payment_due_date).toLocaleString()}</p>
                  )}
                </div>
              )}

              <div className="space-y-3 mb-6">
                {rekenings.map((rek) => (
                  <div key={rek.id} className="p-3 border border-stone-100 bg-stone-50 rounded-lg flex justify-between items-center group">
                    <div>
                      <p className="text-xs text-stone-500 font-semibold uppercase">{rek.bank_name}</p>
                      <p className="font-medium text-stone-800 text-sm font-mono tracking-wider">{rek.account_number}</p>
                      <p className="text-xs text-stone-500">a/n {rek.account_holder}</p>
                    </div>
                    <button onClick={() => {
                      navigator.clipboard.writeText(rek.account_number);
                      toast.success(t("order_detail.copy_rek") || "Account number copied!");
                    }} className="p-2 bg-white border border-stone-200 rounded shadow-sm text-stone-400 hover:text-primary group-hover:border-primary transition-colors">
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="border-t border-stone-100 pt-4">
                <p className="text-sm font-medium text-stone-700 mb-3">{t("orders.upload_proof") || "Sudah transfer?"}</p>

                {hasPendingCurrentPayment ? (
                  <div className="w-full flex items-start gap-3 px-4 py-3 text-sm rounded-lg border border-blue-100 bg-blue-50 text-blue-800">
                    <Clock className="w-4 h-4 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-semibold">{pendingPaymentLabel}</p>
                      <p className="text-xs text-blue-700 mt-1">Bukti pembayaran sudah terkirim dan sedang menunggu verifikasi. Upload baru akan tersedia lagi jika bukti ini ditolak admin.</p>
                    </div>
                  </div>
                ) : (
                  <label className={`w-full flex justify-center items-center gap-2 px-4 py-3 text-sm font-medium rounded-lg transition-colors shadow-sm ${shouldLockSettlementForShipping ? "bg-stone-200 text-stone-500 cursor-not-allowed pointer-events-none" : "bg-stone-900 text-white cursor-pointer hover:bg-stone-800"} ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
                    <Upload className="w-4 h-4" />
                    {uploading ? t("order_detail.uploading") : (
                      isPO ? (!isDPLunas ? t("order_detail.confirm_dp") : t("order_detail.confirm_settlement")) : t("order_detail.confirm_full")
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={uploading || shouldLockSettlementForShipping || hasPendingCurrentPayment}
                      onChange={(e) => handleConfirmPayment(e, currentUploadPaymentType)}
                    />
                  </label>
                )}
              </div>
            </div>
          )}

          {/* Riwayat Konfirmasi Pembayaran */}
          {confirmations.length > 0 && (
            <div className="bg-white border border-stone-200 rounded-xl p-6 shadow-sm">
              <h3 className="font-serif text-lg text-stone-900 mb-4">{t("order_detail.payment_history")}</h3>
              <div className="space-y-4">
                {confirmations.map((conf) => (
                  <div key={conf.id} className="p-3 border border-stone-100 bg-stone-50/50 rounded-lg">
                    <div className="flex justify-between items-start mb-2">
                      <Badge className={`text-[10px] font-bold uppercase ${conf.status === 'verified' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                        conf.status === 'rejected' ? 'bg-red-100 text-red-700 border-red-200' :
                          'bg-amber-100 text-amber-700 border-amber-200'
                        }`}>
                        {conf.status === 'verified' ? t("order_detail.conf_verified") :
                          conf.status === 'rejected' ? t("order_detail.conf_rejected") :
                            t("order_detail.conf_pending")}
                      </Badge>
                      <span className="text-[10px] text-stone-400">{new Date(conf.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-stone-200 rounded-md overflow-hidden shrink-0 border border-stone-200">
                        <img
                          src={getImageUrl(conf.proof_url)}
                          alt="Proof"
                          className="w-full h-full object-cover cursor-pointer"
                          onClick={() => window.open(getImageUrl(conf.proof_url), '_blank')}
                        />
                      </div>
                      <div className="flex-1">
                        <p className="text-[11px] font-semibold text-stone-700 uppercase">{conf.payment_type}</p>
                        {conf.nominal > 0 && <p className="text-xs text-stone-500">{formatPrice(conf.nominal)}</p>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Form Alamat Pengiriman NOT USED
          {(!hasAddress || isAddingAddress) && !isPO && order.status !== "cancelled" && (
            <div className="bg-white border border-[#8A9A86]/30 rounded-xl p-6 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full bg-[#8A9A86]"></div>
              <div className="flex items-center gap-2 mb-4 text-[#8A9A86]">
                <MapPin className="w-5 h-5" />
                <h3 className="font-serif text-lg text-stone-900">{t("order_detail.shipping_title")}</h3>
              </div>

              {hasAddress && !isAddingAddress ? (
                <div className="space-y-3">
                  <div>
                    <p className="text-[10px] font-semibold text-stone-500 uppercase tracking-wider mb-1">Penerima</p>
                    <p className="text-sm font-semibold text-stone-900">{shippingData.name}</p>
                    <p className="text-xs text-stone-500">{shippingData.phone}</p>
                    <p className="text-sm text-stone-700 mt-2 p-3 bg-stone-50 border border-stone-100 rounded-lg">{shippingData.address}</p>
                  </div>
                  {order.sender_type === "dropship" && (
                    <div className="pt-3 border-t border-stone-100">
                      <p className="text-[10px] font-semibold text-stone-500 uppercase tracking-wider mb-1">Pengirim (Dropshipper)</p>
                      <p className="text-sm font-semibold text-stone-900">{order.sender_info?.name}</p>
                      <p className="text-xs text-stone-500">{order.sender_info?.phone}</p>
                    </div>
                  )}
                  <button onClick={() => setIsAddingAddress(true)} className="text-xs font-semibold text-[#8A9A86] hover:underline mt-2">{t("order_detail.edit")}</button>
                </div>
              ) : (
                <div className="space-y-4">
                  {hasAddress && <p className="text-xs text-stone-500">{t("order_detail.shipping_update_desc")}</p>}
                  {!hasAddress && <div className="bg-blue-50 text-blue-700 border border-blue-200 p-3 rounded-lg text-xs leading-relaxed">
                    {t("order_detail.shipping_desc_po")}
                  </div>}

                  <div className="flex gap-4 mb-4">
                    <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                      <input type="radio" name="sender_type" value="self" checked={addressForm.sender_type === "self"} onChange={() => setAddressForm({ ...addressForm, sender_type: "self" })} className="text-[#8A9A86] focus:ring-[#8A9A86]" />
                      Kirim atas nama sendiri
                    </label>
                    <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                      <input type="radio" name="sender_type" value="dropship" checked={addressForm.sender_type === "dropship"} onChange={() => setAddressForm({ ...addressForm, sender_type: "dropship" })} className="text-[#8A9A86] focus:ring-[#8A9A86]" />
                      Kirim sebagai Dropshipper
                    </label>
                  </div>

                  {addressForm.sender_type === "dropship" && (
                    <div className="p-4 bg-stone-50 border border-stone-200 rounded-lg space-y-4 mb-4">
                      <p className="text-[10px] uppercase tracking-wider font-bold text-stone-600">Data Pengirim</p>
                      <div>
                        <label className="text-[10px] uppercase tracking-wider font-semibold text-stone-500 block mb-1">Nama Pengirim</label>
                        <input className="w-full text-sm p-3 bg-white border border-stone-200 rounded-lg focus:border-[#8A9A86] focus:outline-none transition-all" value={addressForm.sender_name} onChange={(e) => setAddressForm({ ...addressForm, sender_name: e.target.value })} placeholder="Nama Olshop / Dropshipper" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-wider font-semibold text-stone-500 block mb-1">No. HP Pengirim</label>
                        <input className="w-full text-sm p-3 bg-white border border-stone-200 rounded-lg focus:border-[#8A9A86] focus:outline-none transition-all" value={addressForm.sender_phone} onChange={(e) => setAddressForm({ ...addressForm, sender_phone: e.target.value })} placeholder="081234..." />
                      </div>
                    </div>
                  )}

                  <div className="space-y-4">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-stone-600">Data Penerima</p>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider font-semibold text-stone-500 block mb-1">{t("order_detail.recipient_name")}</label>
                      <input className="w-full text-sm p-3 bg-stone-50 border border-stone-200 rounded-lg focus:border-[#8A9A86] focus:bg-white focus:outline-none transition-all" value={addressForm.name} onChange={(e) => setAddressForm({ ...addressForm, name: e.target.value })} placeholder="Cth: Budi" />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider font-semibold text-stone-500 block mb-1">{t("order_detail.phone_number")}</label>
                      <input className="w-full text-sm p-3 bg-stone-50 border border-stone-200 rounded-lg focus:border-[#8A9A86] focus:bg-white focus:outline-none transition-all" value={addressForm.phone} onChange={(e) => setAddressForm({ ...addressForm, phone: e.target.value })} placeholder="081234..." />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider font-semibold text-stone-500 block mb-1">{t("order_detail.full_address")}</label>
                      <textarea className="w-full text-sm p-3 bg-stone-50 border border-stone-200 rounded-lg focus:border-[#8A9A86] focus:bg-white focus:outline-none transition-all" rows="3" value={addressForm.address} onChange={(e) => setAddressForm({ ...addressForm, address: e.target.value })} placeholder="Jl. Jend Sudirman No 1..."></textarea>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={handleSaveAddress} className="flex-1 bg-[#8A9A86] text-white text-sm font-semibold rounded-lg py-3 hover:bg-[#768573] transition-colors shadow-sm">{t("order_detail.save_address")}</button>
                    {hasAddress && <button onClick={() => setIsAddingAddress(false)} className="px-6 bg-stone-100 text-stone-600 text-sm font-semibold rounded-lg hover:bg-stone-200 transition-colors">{t("order_detail.cancel")}</button>}
                  </div>
                </div>
              )}
            </div>
          )} */}

          {(hasAddress || (order.shipments && order.shipments.length > 0)) && !isAddingAddress && (
            <div className="space-y-4">
              {order.shipments && order.shipments.length > 0 ? (
                order.shipments.map((ship, idx) => (
                  <div key={ship.id} className="bg-white border border-primary/20 rounded-xl p-5 shadow-sm relative overflow-hidden">
                    <div className="flex items-center gap-2 text-primary mb-3">
                      <MapPin className="w-4 h-4" />
                      <h3 className="font-semibold text-sm">Pengiriman Ke-{idx + 1}</h3>
                    </div>
                    <div className="pl-6 border-l-2 border-primary/20 py-1">
                      <p className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold mb-1">Penerima</p>
                      <p className="font-medium text-sm text-stone-900">{ship.name}</p>
                      <p className="text-xs text-stone-500 mb-2">{ship.phone}</p>
                      <p className="text-xs text-stone-600 bg-stone-50 p-2.5 rounded-lg border border-stone-100">{ship.address}</p>

                      {ship.sender_name && (
                        <div className="mt-3 pt-3 border-t border-stone-100">
                          <p className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold mb-1">Pengirim (Dropshipper)</p>
                          <p className="font-medium text-sm text-stone-900">{ship.sender_name}</p>
                          <p className="text-xs text-stone-500">{ship.sender_phone}</p>
                          {ship.sender_address && (
                            <p className="text-xs text-stone-600 bg-stone-50 p-2.5 rounded-lg border border-stone-100 mt-2">{ship.sender_address}</p>
                          )}
                        </div>
                      )}

                      <div className="mt-3 pt-3 border-t border-stone-100 border-dashed">
                        <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider mb-2">Produk dalam paket ini:</p>
                        <ul className="text-xs text-stone-600 space-y-1 ml-4 list-disc marker:text-stone-300">
                          {order.items?.filter(i => i.shipment_id === ship.id).map(i => (
                            <li key={i.transaction_detail_id}>
                              {getDisplayProductLabel(i.product_name, i.brand_name)}
                              {getDisplayVariantValues(i.variant).length > 0 ? ` (${getDisplayVariantValues(i.variant).join(', ')})` : ""}
                              <span className="font-semibold text-stone-800 ml-1 normal-case">× {i.quantity}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="bg-white border border-stone-200 rounded-xl p-6 shadow-sm relative overflow-hidden">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2 text-stone-600">
                      <MapPin className="w-4 h-4" />
                      <h3 className="font-semibold text-sm">{t("order_detail.shipped_to")}</h3>
                    </div>
                    {!shippingData.awb && isPO && canEditShippingSetup && order.order_status_v2 !== "shipped" && order.order_status_v2 !== "completed" && order.payment_status_v2 !== "cancelled" && (
                      <Link to={`/orders/${order.id}/shipping`} className="text-[11px] font-semibold text-primary hover:underline uppercase tracking-wider">
                        {t("order_detail.edit") || "Ubah"}
                      </Link>
                    )}
                  </div>

                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold mb-1">Penerima</p>
                    <p className="font-medium text-sm text-stone-900">{shippingData.name || "-"}</p>
                    <p className="text-xs text-stone-500 mb-2">{shippingData.phone || "-"}</p>
                    <p className="text-xs text-stone-600 bg-stone-50 p-3 rounded-lg border border-stone-100">{shippingData.address || t("order_detail.no_address_yet") || "Belum ada alamat"}</p>
                  </div>

                  {order.sender_type === "dropship" && (
                    <div className="mt-3 pt-3 border-t border-stone-100">
                      <p className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold mb-1">Pengirim (Dropshipper)</p>
                      <p className="font-medium text-sm text-stone-900">{order.sender_info?.name}</p>
                      <p className="text-xs text-stone-500">{order.sender_info?.phone}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Cancel Action */}
          {order.is_cancellable && order.status !== "cancelled" && (
            <button
              onClick={handleCancelOrder}
              disabled={cancelling}
              className="w-full py-3 flex items-center justify-center gap-2 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all text-xs font-semibold uppercase tracking-widest disabled:opacity-50"
            >
              <XCircle className="w-4 h-4" /> {t("order_detail.cancel_order")}
            </button>
          )}

        </div>

      </div>

      {showSplitModal && (
        <div className="fixed inset-0 bg-stone-900/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col relative shadow-2xl">
            <button onClick={() => setShowSplitModal(false)} className="absolute right-4 top-4 text-stone-400 hover:text-stone-900 z-10">
              <XCircle className="w-5 h-5" />
            </button>

            <div className="p-6 border-b border-stone-100 shrink-0">
              <h2 className="text-lg font-serif font-semibold text-stone-900 mb-2">Atur Pengiriman (Dropship)</h2>
              <p className="text-sm text-stone-500 leading-relaxed">Pilih metode yang paling nyaman untuk mengatur pesanan ini ke satu atau beberapa alamat tujuan pengiriman.</p>
              <div className="flex bg-stone-100 p-1 rounded-lg mt-4 w-fit">
                <button onClick={() => setSplitMode("excel")} className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-all ${splitMode === 'excel' ? 'bg-white shadow text-primary' : 'text-stone-500 hover:text-stone-700'}`}>Via Excel</button>
                <button onClick={() => setSplitMode("manual")} className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-all ${splitMode === 'manual' ? 'bg-white shadow text-primary' : 'text-stone-500 hover:text-stone-700'}`}>Input Manual</button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto">
              {splitMode === "excel" ? (
                <div className="space-y-4 max-w-md mx-auto">
                  <div className="border border-stone-200 hover:border-primary hover:bg-primary/5 transition-colors p-5 rounded-xl cursor-pointer text-center group" onClick={handleDownloadSplitTemplate}>
                    <Download className="w-8 h-8 text-stone-300 group-hover:text-primary group-hover:-translate-y-1 transition-all mx-auto mb-2" />
                    <h3 className="font-semibold text-stone-800 text-sm">1. Download Template Excel</h3>
                    <p className="text-[11px] text-stone-500 mt-1">Dapatkan format produk untuk transaksi ini.</p>
                  </div>

                  <label className="border border-dashed border-primary/30 hover:border-primary hover:bg-primary/5 transition-colors p-5 rounded-xl cursor-pointer bg-stone-50 text-center block group relative overflow-hidden">
                    <Upload className={`w-8 h-8 mx-auto mb-2 transition-all ${splitFile ? 'text-primary' : 'text-stone-300 group-hover:text-primary group-hover:-translate-y-1'}`} />
                    <h3 className="font-semibold text-stone-800 text-sm">{splitFile ? "File Terpilih:" : "2. Upload Data Excel"}</h3>
                    <p className={"text-[11px] mt-1 " + (splitFile ? "text-primary font-medium" : "text-stone-500")}>{splitFile ? splitFile.name : "Klik untuk pilih file (.xlsx)"}</p>
                    <input type="file" accept=".xlsx" className="hidden" onChange={(e) => setSplitFile(e.target.files[0])} />
                  </label>

                  <button
                    disabled={!splitFile || uploadingSplit}
                    onClick={handleUploadSplit}
                    className="w-full mt-6 bg-primary hover:opacity-90 text-primary-foreground py-3 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                  >
                    {uploadingSplit ? "Memproses Data..." : "Simpan Pembagian Pengiriman"}
                  </button>
                </div>
              ) : (
                <div className="flex flex-col lg:flex-row gap-6">
                  <div className="w-full lg:w-1/3 order-2 lg:order-1 bg-stone-50 p-4 rounded-xl border border-stone-200 self-start sticky top-0">
                    <h3 className="font-semibold text-stone-800 mb-3 text-sm">Sisa Stok Belum Dialokasi</h3>
                    <div className="space-y-3">
                      {order?.items?.map(it => {
                        const rem = getRemainingQty(it.transaction_detail_id);
                        if (rem <= 0) return null;
                        return (
                          <div key={it.transaction_detail_id} className="bg-white p-3 rounded-lg border border-stone-100 shadow-sm flex items-center justify-between">
                            <div>
                              <p className="text-xs font-semibold text-stone-800 line-clamp-1" title={it.product_name}>
                                {getDisplayProductLabel(it.product_name, it.brand_name)}
                              </p>
                              {getDisplayVariantValues(it.variant).length > 0 && <p className="text-[10px] text-stone-400">{getDisplayVariantValues(it.variant).join(', ')}</p>}
                            </div>
                            <span className="text-xs font-bold text-primary">Tersisa: {rem}</span>
                          </div>
                        );
                      })}
                      {order?.items?.every(it => getRemainingQty(it.transaction_detail_id) <= 0) && (
                        <p className="text-xs text-stone-500 text-center py-4 bg-white rounded border border-stone-100">Semua produk sudah teralokasi ke penerima!</p>
                      )}
                    </div>
                  </div>
                  <div className="w-full lg:w-2/3 order-1 lg:order-2 space-y-4">
                    {manualShipments.map((ship, idx) => (
                      <div key={ship.id} className="bg-white border border-stone-200 rounded-xl p-4 shadow-sm relative">
                        <button onClick={() => handleRemoveManualShipment(ship.id)} className="absolute top-3 right-3 text-stone-300 hover:text-red-500 bg-white"><XCircle className="w-4 h-4" /></button>
                        <h4 className="font-semibold text-stone-800 text-sm mb-3">Penerima #{idx + 1}</h4>
                        <div className="grid grid-cols-2 gap-3 mb-4">
                          <input value={ship.name} onChange={(e) => handleUpdateManualShipment(ship.id, "name", e.target.value)} placeholder="Nama Penerima *" className="text-xs bg-stone-50 border border-stone-200 rounded p-2 focus:ring-1 outline-none" />
                          <input value={ship.phone} onChange={(e) => handleUpdateManualShipment(ship.id, "phone", e.target.value)} placeholder="Nomor HP *" className="text-xs bg-stone-50 border border-stone-200 rounded p-2 focus:ring-1 outline-none" />
                          <input value={ship.address} onChange={(e) => handleUpdateManualShipment(ship.id, "address", e.target.value)} placeholder="Alamat Lengkap *" className="col-span-2 text-xs bg-stone-50 border border-stone-200 rounded p-2 focus:ring-1 outline-none" />
                        </div>
                        <div className="mb-4">
                          <label className="flex items-center gap-2 text-xs text-stone-700 cursor-pointer mb-2">
                            <input type="checkbox" checked={ship.isDropship} onChange={(e) => handleUpdateManualShipment(ship.id, "isDropship", e.target.checked)} className="text-primary focus:ring-primary" />
                            Kirim sebagai Dropshipper
                          </label>
                          {ship.isDropship && (
                            <div className="flex flex-col gap-3 bg-stone-50 p-3 rounded border border-stone-200">
                              <div className="grid grid-cols-2 gap-3">
                                <input value={ship.sender_name} onChange={(e) => handleUpdateManualShipment(ship.id, "sender_name", e.target.value)} placeholder="Nama Pengirim" className="text-xs bg-white border border-stone-200 rounded p-2 focus:ring-1 outline-none" />
                                <input value={ship.sender_phone} onChange={(e) => handleUpdateManualShipment(ship.id, "sender_phone", e.target.value)} placeholder="No HP Pengirim" className="text-xs bg-white border border-stone-200 rounded p-2 focus:ring-1 outline-none" />
                              </div>
                              <input value={ship.sender_address} onChange={(e) => handleUpdateManualShipment(ship.id, "sender_address", e.target.value)} placeholder="Alamat Pengirim Lengkap (Opsional)" className="w-full text-xs bg-white border border-stone-200 rounded p-2 focus:ring-1 outline-none" />
                            </div>
                          )}
                        </div>
                        <div className="bg-stone-50 p-3 rounded border border-stone-100">
                          <p className="text-[10px] font-semibold text-stone-500 uppercase tracking-wider mb-2">Keranjang Penerima Ini</p>
                          {order?.items?.map(it => {
                            const alloc = ship.items.find(i => i.item_id === it.transaction_detail_id)?.qty || 0;
                            const rem = getRemainingQty(it.transaction_detail_id);
                            const locked = getLockedQty(it.transaction_detail_id);
                            if (locked > 0 && alloc === 0) return null;
                            // Only show if allocated > 0 or remaining > 0 so we can add it
                            if (alloc === 0 && rem <= 0) return null;
                            return (
                              <div key={it.transaction_detail_id} className="flex items-center justify-between py-1.5 border-b border-stone-100 last:border-0">
                                <div className="w-2/3">
                                  <p className="text-xs font-semibold text-stone-700 line-clamp-1">
                                    {getDisplayProductLabel(it.product_name, it.brand_name)}
                                  </p>
                                  {getDisplayVariantValues(it.variant).length > 0 && <p className="text-[9px] text-stone-400">{getDisplayVariantValues(it.variant).join(', ')}</p>}
                                </div>
                                <div className="flex items-center gap-2">
                                  <button onClick={() => handleDecrementManualItem(ship.id, it.transaction_detail_id)} disabled={alloc <= 0} className="w-5 h-5 flex items-center justify-center bg-stone-200 hover:bg-stone-300 rounded text-stone-600 disabled:opacity-30 disabled:cursor-not-allowed">-</button>
                                  <span className="text-xs font-bold w-4 text-center">{alloc}</span>
                                  <button onClick={() => handleIncrementManualItem(ship.id, it.transaction_detail_id)} disabled={rem <= 0} className="w-5 h-5 flex items-center justify-center bg-primary hover:opacity-90 text-primary-foreground rounded disabled:opacity-30 disabled:cursor-not-allowed">+</button>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    ))}

                    <button onClick={handleAddManualShipment} className="w-full border-2 border-dashed border-primary/40 text-primary hover:bg-primary/5 hover:border-primary py-3 rounded-xl font-bold text-sm transition-colors mb-4">+ Tambah Penerima Baru</button>

                    {manualShipments.length > 0 && (
                      <button
                        disabled={uploadingSplit}
                        onClick={handleSubmitManualSplit}
                        className="w-full mt-6 bg-primary hover:opacity-90 text-primary-foreground py-3 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                      >
                        {uploadingSplit ? "Menyimpan Data..." : "Simpan Pecahan Dropship"}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </PageWrapper>
  );
}
